import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Bell,
  CloudOff,
  MinusCircle,
  PencilLine,
  PlusCircle,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../auth';
import { api, ApiClientError } from '../api';
import { enqueue } from '../offline';
import type { Task } from '../types';
import { Loading } from '../components/Loading';
import { useToast } from '../components/Toast';

interface NotificationPreferences {
  dailyLogEnabled: boolean;
  dailyLogTime: string;
  taskEnabled: boolean;
  rewardEnabled: boolean;
  timezone: string;
}

type PointMode = 'reward' | 'penalty';

function signedPoints(value: number): string {
  return `${value > 0 ? '+' : ''}${value}`;
}

export function LogPage() {
  const { selectedChild, selectedChildId } = useAuth();
  const { show } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyTask, setBusyTask] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [customPoints, setCustomPoints] = useState(3);
  const [pointMode, setPointMode] = useState<PointMode>('reward');
  const [offlineCount, setOfflineCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const [taskRows, prefs] = await Promise.all([
        api<Task[]>('/tasks'),
        api<NotificationPreferences>('/notifications/preferences')
      ]);
      setTasks(taskRows.filter((task) => task.active));
      setPreferences(prefs);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể tải nhiệm vụ.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedChildId, show]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  async function submitPoint(
    body: {
      childId: string;
      taskId?: string;
      delta?: number;
      reason?: string;
      idempotencyKey: string;
    },
    successMessage: string
  ) {
    try {
      const result = await api<{ balance: number }>('/points', { method: 'POST', body });
      show(
        `${successMessage} Tổng điểm hiện có: ${new Intl.NumberFormat('vi-VN').format(result.balance)}.`,
        'success'
      );
      window.dispatchEvent(new Event('kidee:points-updated'));
    } catch (error) {
      if (error instanceof ApiClientError && error.code === 'NETWORK_ERROR') {
        await enqueue({
          id: body.idempotencyKey,
          path: '/points',
          method: 'POST',
          body,
          createdAt: new Date().toISOString()
        });
        setOfflineCount((value) => value + 1);
        show('Đã lưu trên điện thoại. Kidee sẽ đồng bộ khi có mạng.', 'info');
        return;
      }
      throw error;
    }
  }

  async function applyTask(task: Task) {
    if (!selectedChildId) return;
    setBusyTask(task.id);
    try {
      const verb = task.points > 0 ? 'cộng' : 'trừ';
      await submitPoint(
        {
          childId: selectedChildId,
          taskId: task.id,
          idempotencyKey: crypto.randomUUID()
        },
        `Đã ${verb} ${Math.abs(task.points)} điểm cho ${selectedChild?.nickname}.`
      );
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể ghi điểm.', 'error');
    } finally {
      setBusyTask('');
    }
  }

  async function addCustom(event: FormEvent) {
    event.preventDefault();
    if (!selectedChildId || !customReason.trim()) return;
    const delta = pointMode === 'reward' ? customPoints : -customPoints;
    setBusyTask('custom');
    try {
      await submitPoint(
        {
          childId: selectedChildId,
          delta,
          reason: customReason.trim(),
          idempotencyKey: crypto.randomUUID()
        },
        pointMode === 'reward'
          ? `Đã cộng ${customPoints} điểm: “${customReason.trim()}”.`
          : `Đã trừ ${customPoints} điểm: “${customReason.trim()}”.`
      );
      setCustomReason('');
      setCustomPoints(3);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể ghi điểm.', 'error');
    } finally {
      setBusyTask('');
    }
  }

  if (loading) return <Loading label="Đang tải các hoạt động…" />;

  return (
    <div className="page log-page">
      <div className="page-title">
        <span className="eyebrow">Dành cho phụ huynh</span>
        <h1>Ghi điểm hôm nay</h1>
        <p>Ghi nhận việc tốt hoặc trừ điểm minh bạch cho {selectedChild?.nickname}.</p>
      </div>

      {preferences && (
        <section className="reminder-banner">
          <span className="reminder-icon"><Bell size={24} /></span>
          <div><strong>Nhắc ghi điểm lúc {preferences.dailyLogTime} mỗi ngày</strong><p>Giúp gia đình duy trì việc ghi nhận đều đặn.</p></div>
          <a href="/install">Đổi giờ</a>
        </section>
      )}

      {offlineCount > 0 && (
        <div className="offline-notice"><CloudOff size={18} /><span>{offlineCount} ghi nhận đang chờ đồng bộ.</span></div>
      )}

      <section>
        <div className="section-heading section-heading--stack">
          <div><span className="section-icon section-icon--orange"><Sparkles size={19} /></span><h2>Ghi nhanh theo nhiệm vụ</h2></div>
          <p>Thẻ xanh cộng điểm; thẻ đỏ trừ điểm. Mỗi lần chạm tạo đúng một giao dịch chống trùng.</p>
        </div>
        <div className="task-grid">
          {tasks.map((task) => (
            <button
              key={task.id}
              type="button"
              className={`task-card ${task.points < 0 ? 'task-card--penalty' : 'task-card--reward'}`}
              disabled={Boolean(busyTask)}
              onClick={() => void applyTask(task)}
            >
              <span className="task-icon">{task.icon}</span>
              <span>
                <strong>{task.title}</strong>
                <b>{signedPoints(task.points)} điểm</b>
              </span>
              {busyTask === task.id && <span className="mini-spinner" />}
            </button>
          ))}
        </div>
      </section>

      <form className={`surface custom-point-form ${pointMode === 'penalty' ? 'custom-point-form--penalty' : ''}`} onSubmit={addCustom}>
        <div className="section-heading section-heading--stack">
          <div><span className="section-icon section-icon--yellow"><PencilLine size={19} /></span><h2>Ghi điểm tự do</h2></div>
          <p>Luôn ghi lý do cụ thể để con hiểu vì sao được cộng hoặc bị trừ.</p>
        </div>

        <div className="point-mode-tabs" role="tablist" aria-label="Chọn cộng hoặc trừ điểm">
          <button type="button" role="tab" aria-selected={pointMode === 'reward'} className={pointMode === 'reward' ? 'active' : ''} onClick={() => setPointMode('reward')}><PlusCircle size={18} /> Cộng điểm</button>
          <button type="button" role="tab" aria-selected={pointMode === 'penalty'} className={pointMode === 'penalty' ? 'active penalty' : ''} onClick={() => setPointMode('penalty')}><MinusCircle size={18} /> Trừ điểm</button>
        </div>

        <label>
          {pointMode === 'reward' ? 'Con đã làm tốt điều gì?' : 'Điều gì cần điều chỉnh?'}
          <textarea
            required
            maxLength={300}
            rows={3}
            value={customReason}
            onChange={(event) => setCustomReason(event.target.value)}
            placeholder={
              pointMode === 'reward'
                ? `${selectedChild?.nickname ?? 'Con'} tự giác học từ mới tiếng Anh 15 phút.`
                : `${selectedChild?.nickname ?? 'Con'} không thực hiện quy tắc đã thống nhất.`
            }
          />
        </label>
        <label className="points-stepper">
          Số điểm {pointMode === 'penalty' ? 'bị trừ' : 'được cộng'}
          <span>
            <button type="button" onClick={() => setCustomPoints((value) => Math.max(1, value - 1))}>−</button>
            <strong>{customPoints}</strong>
            <button type="button" onClick={() => setCustomPoints((value) => Math.min(100, value + 1))}>+</button>
          </span>
        </label>
        <p className="balance-policy-note">Số dư được phép xuống dưới 0; toàn bộ lịch sử cộng/trừ vẫn được lưu trong point ledger.</p>
        <button className={`button button--wide ${pointMode === 'penalty' ? 'button--danger' : 'button--primary'}`} type="submit" disabled={busyTask === 'custom'}>
          {pointMode === 'penalty' ? '➖' : '⭐'} {busyTask === 'custom' ? 'Đang ghi nhận…' : `${pointMode === 'penalty' ? 'Trừ' : 'Cộng'} ${customPoints} điểm cho ${selectedChild?.nickname ?? 'con'}`}
        </button>
      </form>
    </div>
  );
}
